import React, { useEffect, useMemo, useState } from 'react';
import { FiShare2, FiUserPlus, FiTrash2, FiInbox } from 'react-icons/fi';
import { partnerService, courseService, allocationService } from '../services/supabaseClient';
import '../styles/Allocation.css';

const today = () => new Date().toISOString().slice(0, 10);

const kindClass = (type) => {
  const t = (type || '').toLowerCase();
  if (t.includes('cert')) return 'certification';
  if (t.includes('webinar')) return 'webinar';
  if (t.includes('course')) return 'course';
  return 'default';
};

const formatDate = (value) => {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

export default function Allocation() {
  const [partners, setPartners] = useState([]);
  const [content, setContent] = useState([]);
  const [rows, setRows] = useState([]);
  const [rowsLoading, setRowsLoading] = useState(true);

  const [partner, setPartner] = useState('');
  const [item, setItem] = useState('');
  const [seats, setSeats] = useState('');
  const [validFrom, setValidFrom] = useState(today());
  const [validTo, setValidTo] = useState('');

  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [removingId, setRemovingId] = useState(null);

  // DB CONNECTED: partners + courses (for the two dropdowns) + allocations
  // (already joined with partner/course display fields) load together.
  const load = async () => {
    setRowsLoading(true);
    try {
      const [partnerRows, courseRows, allocationRows] = await Promise.all([
        partnerService.getAll(),
        courseService.getAll(),
        allocationService.getAll()
      ]);

      setPartners(partnerRows || []);
      setContent(courseRows || []);
      setRows(allocationRows || []);
    } catch (err) {
      setError(err.message || 'Could not load allocations.');
    } finally {
      setRowsLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // Local lookup maps keyed by real DB ids (partner_id / course_id), used
  // to join partner + course display fields when rendering each row.
  const partnerMap = useMemo(
    () => Object.fromEntries(partners.map(row => [row.partner_id, row])),
    [partners]
  );
  const contentMap = useMemo(
    () => Object.fromEntries(content.map(row => [row.course_id, row])),
    [content]
  );

  const partnerLabel = (row) =>
    row?.company_name || [row?.first_name, row?.last_name].filter(Boolean).join(' ') || '—';

  const add = async () => {
    setError('');

    if (!partner || !item) {
      setError('Select both a partner and content.');
      return;
    }
    if (!seats || Number(seats) <= 0) {
      setError('Enter a valid number of seats.');
      return;
    }
    if (!validFrom || !validTo) {
      setError('Set both a valid-from and valid-to date.');
      return;
    }
    if (validTo < validFrom) {
      setError('Valid-to date cannot be before valid-from date.');
      return;
    }

    const alreadyAllocated = rows.some(
      row => row.partner_id === partner && row.course_id === item
    );
    if (alreadyAllocated) {
      setError('This content is already allocated to the partner.');
      return;
    }

    setLoading(true);
    try {
      await allocationService.add({
        partner_id: partner,
        course_id: item,
        seats: Number(seats),
        seats_used: 0,
        valid_from: validFrom,
        valid_until: validTo,
        status: 'active'
      });

      setPartner('');
      setItem('');
      setSeats('');
      setValidFrom(today());
      setValidTo('');
      await load();
    } catch (err) {
      setError(err.message || 'Could not create allocation.');
    } finally {
      setLoading(false);
    }
  };

  const remove = async (allocationId) => {
    setRemovingId(allocationId);
    setError('');
    try {
      await allocationService.remove(allocationId);
      await load();
    } catch (err) {
      setError(err.message || 'Could not remove allocation.');
    } finally {
      setRemovingId(null);
    }
  };

  return (
    <>
      <div className="hero allocation-hero">
        <div className="allocation-hero-left">
          <span className="allocation-icon-badge"><FiShare2 /></span>
          <div>
            <span className="eyebrow">Allocation</span>
            <h2>Allocation</h2>
            <p>Grant a partner the right to register people, with seat and validity limits.</p>
          </div>
        </div>
      </div>

      <div className="panel allocation-form-panel">
        <div className="allocation-grid">
          <div className="allocation-field">
            <label>Partner</label>
            <select value={partner} onChange={event => setPartner(event.target.value)}>
              <option value="">Select Partner</option>
              {partners.map(row => (
                <option key={row.partner_id} value={row.partner_id}>{partnerLabel(row)}</option>
              ))}
            </select>
          </div>

          <div className="allocation-field">
            <label>Content</label>
            <select value={item} onChange={event => setItem(event.target.value)}>
              <option value="">Select Course / Certification</option>
              {content.map(row => (
                <option key={row.course_id} value={row.course_id}>
                  {row.name} · {row.type} - {row.course_code}
                </option>
              ))}
            </select>
          </div>

          <div className="allocation-field">
            <label>Seats</label>
            <input
              type="number"
              min="1"
              placeholder="e.g. 50"
              value={seats}
              onChange={event => setSeats(event.target.value)}
            />
          </div>

          <div className="allocation-field">
            <label>Valid from</label>
            <input
              type="date"
              value={validFrom}
              onChange={event => setValidFrom(event.target.value)}
            />
          </div>

          <div className="allocation-field">
            <label>Valid to</label>
            <input
              type="date"
              value={validTo}
              onChange={event => setValidTo(event.target.value)}
            />
          </div>

          <button className="primary allocation-submit" onClick={add} type="button" disabled={loading}>
            <FiUserPlus />
            {loading ? 'Allocating...' : 'Allocate'}
          </button>
        </div>

        {error && <div className="error">{error}</div>}

        <p className="allocation-note">
          Partner-created courses are auto-allocated to their creator and don&apos;t appear here.
        </p>
      </div>

      <div className="table-wrap allocation-table">
        <table>
          <thead>
            <tr>
              <th>Partner</th>
              <th>Content</th>
              <th>Kind</th>
              <th>Seats Used</th>
              <th>Validity</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {rowsLoading && (
              <tr><td colSpan={7} className="empty">Loading allocations…</td></tr>
            )}
            {!rowsLoading && rows.map(row => {
              const p = row.partners || partnerMap[row.partner_id];
              const c = row.course || contentMap[row.course_id];
              const used = row.seats_used ?? 0;
              const total = row.seats ?? 0;
              const pct = total ? Math.min(100, (used / total) * 100) : 0;

              return (
                <tr key={row.allocation_id}>
                  <td><b>{partnerLabel(p)}</b></td>
                  <td className="allocation-content-cell">
                    <b>{c?.name || '—'}</b>
                    <small>{c?.course_code || c?.course_id || ''}</small>
                  </td>
                  <td>
                    <span className={`kind-badge ${kindClass(c?.type)}`}>{c?.type || '—'}</span>
                  </td>
                  <td className="seat-used-cell">
                    <span className="seat-used-value">{used} / {total || '—'}</span>
                    <div className="seat-bar">
                      <div className="seat-bar-fill" style={{ width: `${pct}%` }} />
                    </div>
                  </td>
                  <td className="allocation-validity">
                    {formatDate(row.valid_from)} → {formatDate(row.valid_until)}
                  </td>
                  <td><span className={`status ${row.status}`}>{row.status}</span></td>
                  <td>
                    <button
                      className="allocation-remove-btn"
                      onClick={() => remove(row.allocation_id)}
                      type="button"
                      disabled={removingId === row.allocation_id}
                    >
                      <FiTrash2 />
                      {removingId === row.allocation_id ? 'Removing...' : 'Remove'}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {!rowsLoading && !rows.length && (
          <div className="empty-row allocation-empty">
            <FiInbox />
            <span>No allocations yet.</span>
          </div>
        )}
      </div>
    </>
  );
}